import React, { useEffect, useState } from "react";
import { api, fmtDateTime } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Copy, Trash2, UserPlus, Check, Mail } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { value: "admin", label: "Administrador" },
  { value: "office", label: "Back-Office" },
  { value: "operator", label: "Operador" },
];

export default function Users() {
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "operator" });
  const [invitedLink, setInvitedLink] = useState(null);

  const fetchAll = async () => {
    const [u, i] = await Promise.all([api.get("/users"), api.get("/invites")]);
    setUsers(u.data);
    setInvites(i.data);
  };

  useEffect(() => { fetchAll(); }, []);

  const sendInvite = async () => {
    try {
      const { data } = await api.post("/auth/invite", form);
      setInvitedLink(data.invite_link);
      setForm({ email: "", name: "", role: "operator" });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao convidar");
    }
  };

  const removeUser = async (uid) => {
    if (!window.confirm("Excluir este usuário?")) return;
    try {
      await api.delete(`/users/${uid}`);
      toast.success("Usuário excluído");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success("Link copiado");
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1200px] mx-auto">
      <div className="flex justify-between items-end mb-6">
        <div>
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500">Administração</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-slate-950">Usuários</h1>
          <p className="text-sm text-slate-600 mt-1">Convide novos operadores e back-office por link seguro.</p>
        </div>
        <Button onClick={() => { setInvitedLink(null); setOpen(true); }} className="h-12 bg-[#0052CC] hover:bg-[#003D99] text-white rounded-sm" data-testid="invite-user-button">
          <UserPlus className="w-4 h-4 mr-2" /> Convidar Usuário
        </Button>
      </div>

      <Card className="rounded-sm border-slate-200 overflow-hidden mb-8">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 text-xs font-bold tracking-[0.18em] uppercase text-slate-500">
          Ativos · {users.length}
        </div>
        <table className="w-full text-sm">
          <thead className="bg-white border-b border-slate-200">
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="py-3 px-4 font-semibold">Nome</th>
              <th className="py-3 px-4 font-semibold">E-mail</th>
              <th className="py-3 px-4 font-semibold">Perfil</th>
              <th className="py-3 px-4 font-semibold">Criado em</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-slate-100" data-testid={`user-row-${u.email}`}>
                <td className="py-3 px-4 text-slate-900 font-medium">{u.name}</td>
                <td className="py-3 px-4 text-slate-700 font-mono text-xs">{u.email}</td>
                <td className="py-3 px-4">
                  <span className="text-[10px] font-bold tracking-wider uppercase border border-slate-300 px-2 py-0.5 rounded-sm">{u.role}</span>
                </td>
                <td className="py-3 px-4 text-xs font-mono text-slate-500">{fmtDateTime(u.created_at)}</td>
                <td className="py-3 px-4 text-right">
                  <Button variant="ghost" size="sm" onClick={() => removeUser(u.id)} className="text-red-600 hover:text-red-700" data-testid={`delete-user-${u.email}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card className="rounded-sm border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 text-xs font-bold tracking-[0.18em] uppercase text-slate-500">
          Convites · {invites.length}
        </div>
        <table className="w-full text-sm">
          <thead className="bg-white border-b border-slate-200">
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="py-3 px-4 font-semibold">E-mail</th>
              <th className="py-3 px-4 font-semibold">Nome</th>
              <th className="py-3 px-4 font-semibold">Perfil</th>
              <th className="py-3 px-4 font-semibold">Status</th>
              <th className="py-3 px-4 font-semibold">Link</th>
            </tr>
          </thead>
          <tbody>
            {invites.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">Nenhum convite enviado.</td></tr>}
            {invites.map((inv) => {
              const link = `${window.location.origin}/login?token=${inv.token}`;
              return (
                <tr key={inv.token} className="border-b border-slate-100">
                  <td className="py-3 px-4 font-mono text-xs">{inv.email}</td>
                  <td className="py-3 px-4">{inv.name}</td>
                  <td className="py-3 px-4 text-xs uppercase tracking-wider">{inv.role}</td>
                  <td className="py-3 px-4">
                    {inv.used ? (
                      <span className="text-xs text-emerald-700 inline-flex items-center gap-1"><Check className="w-3 h-3" /> Aceito</span>
                    ) : (
                      <span className="text-xs text-amber-700">Pendente</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    {!inv.used && (
                      <Button size="sm" variant="ghost" onClick={() => copyLink(link)} className="text-xs">
                        <Copy className="w-3.5 h-3.5 mr-1" /> Copiar
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Convidar Usuário</DialogTitle>
          </DialogHeader>
          {!invitedLink ? (
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-bold tracking-wider uppercase text-slate-500">E-mail</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 rounded-sm" data-testid="invite-email-input" />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-wider uppercase text-slate-500">Nome</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-sm" data-testid="invite-name-input" />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-wider uppercase text-slate-500">Perfil</Label>
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="w-full h-11 border border-slate-300 rounded-sm px-2 bg-white"
                  data-testid="invite-role-select"
                >
                  {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm p-3 rounded-sm">
                Convite criado. Compartilhe o link abaixo:
              </div>
              <div className="bg-slate-50 border border-slate-200 p-3 rounded-sm font-mono text-xs break-all" data-testid="invite-link-display">{invitedLink}</div>
              <Button onClick={() => { navigator.clipboard.writeText(invitedLink); toast.success("Copiado"); }} variant="outline" className="w-full rounded-sm">
                <Copy className="w-4 h-4 mr-2" /> Copiar Link
              </Button>
            </div>
          )}
          <DialogFooter>
            {!invitedLink ? (
              <>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancelar</Button>
                <Button onClick={sendInvite} className="bg-[#0052CC] hover:bg-[#003D99] text-white rounded-sm" data-testid="send-invite-button">
                  <Mail className="w-4 h-4 mr-2" /> Gerar Convite
                </Button>
              </>
            ) : (
              <Button onClick={() => setOpen(false)} className="bg-[#0052CC] hover:bg-[#003D99] text-white rounded-sm">Concluir</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
